import React, { useState, useRef, useCallback, useEffect } from 'react';
import ReactDOM from 'react-dom';
import { useTheme } from '../theme';

const ARROW_SIZE = 6;
const OFFSET = 8;
const FADE_DURATION = 150;

function TooltipBubble({ text, targetRect, position }) {
  const { colors } = useTheme();
  const bubbleRef = useRef(null);
  const [coords, setCoords] = useState({ top: 0, left: 0, actualPosition: position });
  const [visible, setVisible] = useState(false);

  useEffect(() => {
    if (!bubbleRef.current || !targetRect) return;
    const bubble = bubbleRef.current.getBoundingClientRect();
    const vw = window.innerWidth;
    const vh = window.innerHeight;

    let top = 0;
    let left = 0;
    let actualPosition = position;

    // Calculate preferred position
    const positions = {
      top: {
        top: targetRect.top - bubble.height - OFFSET - ARROW_SIZE,
        left: targetRect.left + targetRect.width / 2 - bubble.width / 2,
      },
      bottom: {
        top: targetRect.bottom + OFFSET + ARROW_SIZE,
        left: targetRect.left + targetRect.width / 2 - bubble.width / 2,
      },
      left: {
        top: targetRect.top + targetRect.height / 2 - bubble.height / 2,
        left: targetRect.left - bubble.width - OFFSET - ARROW_SIZE,
      },
      right: {
        top: targetRect.top + targetRect.height / 2 - bubble.height / 2,
        left: targetRect.right + OFFSET + ARROW_SIZE,
      },
    };

    const preferred = positions[actualPosition];
    top = preferred.top;
    left = preferred.left;

    // Auto-reposition if out of viewport
    if (actualPosition === 'top' && top < 4) {
      actualPosition = 'bottom';
      top = positions.bottom.top;
      left = positions.bottom.left;
    } else if (actualPosition === 'bottom' && top + bubble.height > vh - 4) {
      actualPosition = 'top';
      top = positions.top.top;
      left = positions.top.left;
    } else if (actualPosition === 'left' && left < 4) {
      actualPosition = 'right';
      top = positions.right.top;
      left = positions.right.left;
    } else if (actualPosition === 'right' && left + bubble.width > vw - 4) {
      actualPosition = 'left';
      top = positions.left.top;
      left = positions.left.left;
    }

    // Clamp horizontal
    if (left < 8) left = 8;
    if (left + bubble.width > vw - 8) left = vw - bubble.width - 8;
    // Clamp vertical
    if (top < 8) top = 8;
    if (top + bubble.height > vh - 8) top = vh - bubble.height - 8;

    setCoords({ top, left, actualPosition });
    // Trigger fade-in after positioning
    requestAnimationFrame(() => setVisible(true));
  }, [targetRect, position]);

  const arrowStyle = (() => {
    const base = {
      position: 'absolute',
      width: 0,
      height: 0,
      borderStyle: 'solid',
    };
    const bg = colors.surface === '#FFFFFF' ? '#1a1a2e' : '#2d2d44';
    switch (coords.actualPosition) {
      case 'top':
        return { ...base, bottom: -ARROW_SIZE, left: '50%', marginLeft: -ARROW_SIZE,
          borderWidth: `${ARROW_SIZE}px ${ARROW_SIZE}px 0 ${ARROW_SIZE}px`,
          borderColor: `${bg} transparent transparent transparent`,
        };
      case 'bottom':
        return { ...base, top: -ARROW_SIZE, left: '50%', marginLeft: -ARROW_SIZE,
          borderWidth: `0 ${ARROW_SIZE}px ${ARROW_SIZE}px ${ARROW_SIZE}px`,
          borderColor: `transparent transparent ${bg} transparent`,
        };
      case 'left':
        return { ...base, right: -ARROW_SIZE, top: '50%', marginTop: -ARROW_SIZE,
          borderWidth: `${ARROW_SIZE}px 0 ${ARROW_SIZE}px ${ARROW_SIZE}px`,
          borderColor: `transparent transparent transparent ${bg}`,
        };
      case 'right':
        return { ...base, left: -ARROW_SIZE, top: '50%', marginTop: -ARROW_SIZE,
          borderWidth: `${ARROW_SIZE}px ${ARROW_SIZE}px ${ARROW_SIZE}px 0`,
          borderColor: `transparent ${bg} transparent transparent`,
        };
      default:
        return base;
    }
  })();

  const bgColor = colors.surface === '#FFFFFF' ? '#1a1a2e' : '#2d2d44';

  return ReactDOM.createPortal(
    <div
      ref={bubbleRef}
      style={{
        position: 'fixed',
        top: coords.top,
        left: coords.left,
        zIndex: 10000,
        maxWidth: 280,
        padding: '10px 14px',
        background: bgColor,
        color: '#f0f0f0',
        fontFamily: "'IBM Plex Mono', monospace",
        fontSize: 11,
        lineHeight: 1.55,
        borderRadius: 8,
        boxShadow: '0 4px 20px rgba(0,0,0,0.25)',
        pointerEvents: 'none',
        opacity: visible ? 1 : 0,
        transition: `opacity ${FADE_DURATION}ms ease`,
        wordWrap: 'break-word',
      }}
    >
      {text}
      <div style={arrowStyle} />
    </div>,
    document.body
  );
}

export default function Tooltip({ text, children, position = 'top' }) {
  const [show, setShow] = useState(false);
  const [targetRect, setTargetRect] = useState(null);
  const wrapperRef = useRef(null);
  const timerRef = useRef(null);

  const handleEnter = useCallback(() => {
    timerRef.current = setTimeout(() => {
      if (wrapperRef.current) {
        setTargetRect(wrapperRef.current.getBoundingClientRect());
        setShow(true);
      }
    }, 200);
  }, []);

  const handleLeave = useCallback(() => {
    clearTimeout(timerRef.current);
    setShow(false);
  }, []);

  useEffect(() => {
    return () => clearTimeout(timerRef.current);
  }, []);

  return (
    <span
      ref={wrapperRef}
      onMouseEnter={handleEnter}
      onMouseLeave={handleLeave}
      style={{ display: 'inline-flex', alignItems: 'center' }}
    >
      {children}
      {show && targetRect && <TooltipBubble text={text} targetRect={targetRect} position={position} />}
    </span>
  );
}

export function InfoIcon({ text, position = 'top', colors }) {
  return (
    <Tooltip text={text} position={position}>
      <span
        style={{
          display: 'inline-flex',
          alignItems: 'center',
          justifyContent: 'center',
          width: 14,
          height: 14,
          borderRadius: '50%',
          fontSize: 9,
          fontFamily: "'IBM Plex Mono', monospace",
          fontWeight: 600,
          color: colors ? colors.labelMuted : '#999',
          border: `1px solid ${colors ? colors.borderLight : '#ddd'}`,
          cursor: 'help',
          userSelect: 'none',
          lineHeight: 1,
          flexShrink: 0,
        }}
      >
        i
      </span>
    </Tooltip>
  );
}
